/* <antarti-popup>: opens the newsletter dialog once per visitor (delay or scroll depth),
   remembers subscribed / dismissed on the device, stays out of the way of other dialogs,
   and in the theme editor opens only while its section is selected.
   Over the home film (<antarti-seq>): while the film is on screen both triggers wait; the delay
   starts again once it scrolls off, and the depth is measured from the film's end. */

(() => {
  'use strict';

  const KEY = 'antarti.newsletter.v1';
  const DAY = 86400000;

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || null;
    } catch {
      return null;
    }
  };
  const write = (state) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
      /* private mode — the popup will simply show again next visit */
    }
  };

  class AntartiPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.days = Number(this.dataset.days) || 30;
      this.fired = false;
      this.onScroll = this.onScroll.bind(this);

      this.dialog.addEventListener('close', () => {
        if (!this.subscribed) write('dismissed');
        this.teardown();
      });

      // Back from a successful post: Shopify returns to ?customer_posted=true#<form id>.
      const success = this.querySelector('[data-popup-success]');
      const mine = location.hash === `#${this.dataset.formId}`;
      if (success) {
        write('subscribed');
        if (mine) this.open(true);
        return;
      }
      const error = this.querySelector('[data-popup-error]');
      if (error && mine) {
        this.open(true);
        return;
      }
      // Shopify shows a customer form's errors on every customer form on the page. An error
      // from another form (the band) is not ours: clear it so a timed open starts clean.
      if (error) {
        const input = this.querySelector('.popup__form input[type="email"]');
        input?.removeAttribute('aria-invalid');
        input?.removeAttribute('aria-describedby');
        error.remove();
      }

      if (window.Shopify?.designMode) {
        this.onSelect = (e) => {
          if (e.detail?.sectionId === this.dataset.section) this.open(true);
        };
        this.onDeselect = (e) => {
          if (e.detail?.sectionId === this.dataset.section && this.dialog.open) this.dialog.close();
        };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
        return;
      }

      if (this.dataset.enabled !== 'true') return;
      if (['password', 'cart', 'gift_card'].includes(this.dataset.pageType)) return;
      if (this.seen()) return;
      // The Spotlight home page has its own sign-up band and no popup (the approved mock-up).
      if (document.querySelector('antarti-seq')) return;

      const delay = (Number(this.dataset.delay) || 20) * 1000;
      this.timer = setTimeout(() => this.fire(), delay);
      this.depth = (Number(this.dataset.depth) || 40) / 100;
      // A restored or deep-linked scroll position shouldn't fire it on arrival: arm after 3 s.
      this.armTimer = setTimeout(() => {
        this.armed = true;
        this.onScroll();
      }, 3000);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.teardown();
      if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
      if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
    }

    seen() {
      const v = read();
      if (!v) return false;
      if (v.state === 'subscribed') return true;
      return Date.now() - v.at < this.days * DAY;
    }

    onScroll() {
      if (!this.armed) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= this.depth) this.fire();
    }

    fire() {
      if (this.fired) return;
      // Another dialog (bag drawer, lightbox, size guide) has the floor — try again shortly.
      if (document.querySelector('dialog[open]') || document.visibilityState !== 'visible') {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.fire(), 3000);
        return;
      }
      this.fired = true;
      this.teardown();
      this.open(false);
    }

    open(force) {
      if (this.dialog.open) return;
      if (!force && document.querySelector('dialog[open]')) return;
      this.dialog.showModal();
      this.subscribed = !!this.querySelector('[data-popup-success]');
      // Desktop: straight into the email field. Touch: the heading, so no keyboard jumps up.
      const fine = window.matchMedia('(pointer: fine)').matches;
      const heading = this.dialog.querySelector('.popup__heading');
      if (heading) heading.tabIndex = -1;
      const focus =
        this.dialog.querySelector('[data-popup-success]') ||
        (fine ? this.dialog.querySelector('.popup__form input[type="email"]') : heading) ||
        this.dialog.querySelector('.icon-btn');
      focus?.focus();
    }

    teardown() {
      clearTimeout(this.timer);
      clearTimeout(this.armTimer);
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  if (!customElements.get('antarti-popup')) customElements.define('antarti-popup', AntartiPopup);
})();
